import * as SelectPrimitive from '@radix-ui/react-select';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { CheckIcon } from '../../../icons/check';
import { ChevronDownIcon } from '../../../icons/chevron-down';
import { cn } from '../../../lib/utils';

/*
 * Select do DS (padrão shadcn/ui sobre `@radix-ui/react-select`).
 *
 * Substitui o `NativeSelect` onde a lista precisa seguir o Figma (EU-761): o
 * trigger tem a mesma caixa do `Input` (h-9, rounded-md, shadow-2xs, text-sm)
 * e a lista é desenhada pelo DS, não pelo sistema operacional.
 */

const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

const selectTriggerVariants = cva(
  [
    'flex h-9 w-full cursor-pointer items-center justify-between gap-2 rounded-md border bg-background px-3 py-2 text-left text-sm text-foreground shadow-2xs',
    'outline-none transition-colors',
    'focus-visible:ring-2 focus-visible:ring-primary/40',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'data-[placeholder]:text-muted-foreground',
    '[&>span]:line-clamp-1 [&>span]:min-w-0 [&>span]:flex-1',
    // `aria-invalid` sozinho também liga o estilo de erro, para que o campo
    // ligado a um FormField fique vermelho sem repetir `variant`.
    'aria-invalid:border-destructive aria-invalid:focus-visible:ring-destructive/40',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'border-input',
        destructive: 'border-destructive focus-visible:ring-destructive/40',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface SelectTriggerProps
  extends
    ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>,
    VariantProps<typeof selectTriggerVariants> {}

const SelectTrigger = forwardRef<ElementRef<typeof SelectPrimitive.Trigger>, SelectTriggerProps>(
  ({ className, variant, children, 'aria-invalid': ariaInvalid, ...props }, ref) => (
    <SelectPrimitive.Trigger
      ref={ref}
      data-slot="select-trigger"
      aria-invalid={ariaInvalid ?? (variant === 'destructive' ? true : undefined)}
      className={cn(selectTriggerVariants({ variant }), className)}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon
          data-slot="select-icon"
          className="size-4 shrink-0 text-muted-foreground"
        />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
);
SelectTrigger.displayName = 'SelectTrigger';

const SelectContent = forwardRef<
  ElementRef<typeof SelectPrimitive.Content>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', sideOffset = 4, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      data-slot="select-content"
      position={position}
      sideOffset={sideOffset}
      className={cn(
        'relative z-50 min-w-[8rem] overflow-hidden rounded-md border border-border bg-background text-foreground shadow-md',
        position === 'popper' &&
          'max-h-[min(var(--radix-select-content-available-height),16rem)] w-[var(--radix-select-trigger-width)]',
        className
      )}
      {...props}
    >
      <SelectPrimitive.Viewport
        data-slot="select-viewport"
        className={cn(
          'overflow-y-auto p-1',
          position === 'popper' &&
            'max-h-[inherit] w-full min-w-[var(--radix-select-trigger-width)]'
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = 'SelectContent';

const SelectLabel = forwardRef<
  ElementRef<typeof SelectPrimitive.Label>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    data-slot="select-label"
    className={cn('px-2 py-1.5 text-xs font-medium text-muted-foreground', className)}
    {...props}
  />
));
SelectLabel.displayName = 'SelectLabel';

const SelectItem = forwardRef<
  ElementRef<typeof SelectPrimitive.Item>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    data-slot="select-item"
    className={cn(
      // min-h-11 = 44px: alvo de toque mínimo (WCAG 2.5.5 / diretriz mobile).
      'relative flex min-h-11 w-full cursor-pointer select-none items-center gap-2 rounded-sm py-2 pl-3 pr-8 text-sm text-foreground outline-none transition-colors',
      'focus:bg-secondary data-[highlighted]:bg-secondary',
      'data-[state=checked]:font-medium data-[state=checked]:text-primary',
      'data-[disabled]:pointer-events-none data-[disabled]:text-muted-foreground data-[disabled]:opacity-50',
      className
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <span className="absolute right-2 flex size-4 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <CheckIcon data-slot="select-item-check" className="size-4 text-primary" />
      </SelectPrimitive.ItemIndicator>
    </span>
  </SelectPrimitive.Item>
));
SelectItem.displayName = 'SelectItem';

const SelectSeparator = forwardRef<
  ElementRef<typeof SelectPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    data-slot="select-separator"
    className={cn('-mx-1 my-1 h-px bg-border', className)}
    {...props}
  />
));
SelectSeparator.displayName = 'SelectSeparator';

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  selectTriggerVariants,
};
